import React, { useState, useEffect } from 'react';
import './App.css';
import Preloader from './components/Preloader';
import Login from './pages/Login';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import AIAssistant from './components/AIAssistant';
import Dashboard from './pages/Dashboard';
import Policies from './pages/Policies';
import NewPolicies from './pages/NewPolicies';
import PendingApproval from './pages/PendingApproval';
import ExpiredPolicies from './pages/ExpiredPolicies';
import EditPolicy from './pages/EditPolicy';
import Products from './pages/Products';
import UnderwritingCompanies from './pages/UnderwritingCompanies';
import Claims from './pages/Claims';
import NewClaims from './pages/NewClaims';
import OngoingClaims from './pages/OngoingClaims';
import RejectedClaims from './pages/RejectedClaims';
import SettledClaims from './pages/SettledClaims';
import Clients from './pages/Clients';
import Admins from './pages/Admins';
import Reports from './pages/Reports';
import CreatePolicy from './pages/CreatePolicy';
import PendingEndorsements from './pages/PendingEndorsements';
import ApprovedEndorsements from './pages/ApprovedEndorsements';
import PendingCancellations from './pages/PendingCancellations';
import ApprovedCancellations from './pages/ApprovedCancellations';
import ExtendPolicy from './pages/ExtendPolicy';
import NewExtensions from './pages/NewExtensions';
import PendingExtensions from './pages/PendingExtensions';
import ApprovedExtensions from './pages/ApprovedExtensions';
import Profile from './pages/Profile';
import Declarations from './pages/Declarations';
import PostedDeclarations from './pages/PostedDeclarations';
import PendingPremiumSubmissions from './pages/PendingPremiumSubmissions';

function App() {
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [editPolicyId, setEditPolicyId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userData, setUserData] = useState({
    username: '',
    email: '',
    role: null
  });

  useEffect(() => {
    // Check if user is already logged in
    const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const savedUsername = localStorage.getItem('username');
    const savedEmail = localStorage.getItem('userEmail');
    const savedRole = localStorage.getItem('userRole');

    if (loggedIn && savedUsername && savedRole) {
      setIsLoggedIn(true);
      setUserData({
        username: savedUsername,
        email: savedEmail,
        role: parseInt(savedRole)
      });

      // Restore the page the user was on before a reload, falling back to the role default
      const savedActiveMenu = localStorage.getItem('activeMenu');
      if (savedActiveMenu) {
        setActiveMenu(savedActiveMenu);
      } else if (parseInt(savedRole) === 2) {
        setActiveMenu('new-policies');
      }

      const savedEditPolicyId = localStorage.getItem('editPolicyId');
      if (savedEditPolicyId) {
        setEditPolicyId(savedEditPolicyId);
      }
    }

    // Simulate preloader
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  // Persist the current page across reloads
  useEffect(() => {
    if (isLoggedIn) {
      localStorage.setItem('activeMenu', activeMenu);
    }
  }, [activeMenu, isLoggedIn]);

  useEffect(() => {
    if (editPolicyId) {
      localStorage.setItem('editPolicyId', editPolicyId);
    } else {
      localStorage.removeItem('editPolicyId');
    }
  }, [editPolicyId]);

  const handleLogin = (user) => {
    setUserData({
      username: user.username,
      email: user.email,
      role: user.role
    });
    setIsLoggedIn(true);

    // Set initial page based on role
    if (user.role === 2) {
      setActiveMenu('new-policies');
    } else {
      setActiveMenu('dashboard');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('username');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('activeMenu');
    localStorage.removeItem('editPolicyId');
    setIsLoggedIn(false);
    setUserData({ username: '', email: '', role: null });
    setActiveMenu('dashboard');
    setEditPolicyId(null);
  };

  const handleSetActiveMenu = (menu, policyId = null) => {
    setActiveMenu(menu);
    if (policyId) {
      setEditPolicyId(policyId);
    }
  };

  const renderPage = () => {
    // Role-based page rendering
    // Role 1: Full access to all pages
    // Role 2: Only policies, endorsements, and cancellations access

    if (userData.role === 2) {
      // Limited access - policies, endorsements, cancellations, and create policy
      switch (activeMenu) {
        case 'new-policies':
          return <NewPolicies setActiveMenu={handleSetActiveMenu} />;
        case 'edit-policy':
          return <EditPolicy policyId={editPolicyId} setActiveMenu={setActiveMenu} />;
        case 'pending-approval':
          return <PendingApproval userRole={userData.role} />;
        case 'approved-policies':
          return <Policies setActiveMenu={setActiveMenu} userRole={userData.role} />;
        case 'expired-policies':
          return <ExpiredPolicies setActiveMenu={setActiveMenu} />;
        case 'new-declarations':
          return <Declarations />;
        case 'posted-declarations':
          return <PostedDeclarations />;
        case 'create-policy':
          return <CreatePolicy setActiveMenu={setActiveMenu} />;
        case 'pending-endorsements':
          return <PendingEndorsements userRole={userData.role} />;
        case 'approved-endorsements':
          return <ApprovedEndorsements userRole={userData.role} />;
        case 'pending-cancellations':
          return <PendingCancellations userRole={userData.role} />;
        case 'approved-cancellations':
          return <ApprovedCancellations userRole={userData.role} />;
        case 'extend-policy':
          return <ExtendPolicy policyId={editPolicyId} setActiveMenu={setActiveMenu} />;
        case 'new-extensions':
          return <NewExtensions setActiveMenu={handleSetActiveMenu} />;
        case 'pending-extensions':
          return <PendingExtensions userRole={userData.role} />;
        case 'approved-extensions':
          return <ApprovedExtensions />;
        case 'profile':
          return <Profile />;
        default:
          return <NewPolicies setActiveMenu={handleSetActiveMenu} />;
      }
    }

    // Role 1 - Full access
    switch (activeMenu) {
      case 'dashboard':
        return <Dashboard />;
      case 'new-policies':
        return <NewPolicies setActiveMenu={handleSetActiveMenu} />;
      case 'edit-policy':
        return <EditPolicy policyId={editPolicyId} setActiveMenu={setActiveMenu} />;
      case 'pending-approval':
        return <PendingApproval userRole={userData.role} />;
      case 'expired-policies':
        return <ExpiredPolicies setActiveMenu={setActiveMenu} />;
      case 'approved-policies':
        return <Policies setActiveMenu={setActiveMenu} userRole={userData.role} />;
      case 'new-declarations':
        return <Declarations />;
      case 'posted-declarations':
        return <PostedDeclarations />;
      case 'products':
        return <Products setActiveMenu={setActiveMenu} userRole={userData.role} />;
      case 'underwriting-companies':
        return <UnderwritingCompanies />;
      case 'create-policy':
        return <CreatePolicy setActiveMenu={setActiveMenu} />;
      case 'pending-endorsements':
        return <PendingEndorsements userRole={userData.role} />;
      case 'approved-endorsements':
        return <ApprovedEndorsements userRole={userData.role} />;
      case 'pending-cancellations':
        return <PendingCancellations userRole={userData.role} />;
      case 'approved-cancellations':
        return <ApprovedCancellations userRole={userData.role} />;
      case 'extend-policy':
        return <ExtendPolicy policyId={editPolicyId} setActiveMenu={setActiveMenu} />;
      case 'new-extensions':
        return <NewExtensions setActiveMenu={handleSetActiveMenu} />;
      case 'pending-extensions':
        return <PendingExtensions userRole={userData.role} />;
      case 'approved-extensions':
        return <ApprovedExtensions />;
      case 'pending-premium-submissions':
        return <PendingPremiumSubmissions />;
      case 'claims':
        return <Claims userRole={userData.role} />;
      case 'new-claims':
        return <NewClaims userRole={userData.role} />;
      case 'ongoing-claims':
        return <OngoingClaims userRole={userData.role} />;
      case 'rejected-claims':
        return <RejectedClaims userRole={userData.role} />;
      case 'settled-claims':
        return <SettledClaims userRole={userData.role} />;
      case 'clients':
        return <Clients />;
      case 'admins':
        return <Admins />;
      case 'reports':
        return <Reports />;
      case 'profile':
        return <Profile />;
      default:
        return <Dashboard />;
    }
  };

  if (loading) {
    return <Preloader />;
  }

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <Sidebar 
        activeMenu={activeMenu} 
        setActiveMenu={handleSetActiveMenu}
        userRole={userData.role}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
      />
      <div className="main-content">
        <Topbar 
          onLogout={handleLogout}
          username={userData.username}
          userRole={userData.role}
          onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />
        <div className="page-content">
          {renderPage()}
        </div>
      </div>
      <AIAssistant 
        userEmail={userData.email} 
        userRole={userData.role}
      />
    </div>
  );
}

export default App;
